import type { ControllerSession } from '../controller.js';

export function CheckInView({ session }: { session: ControllerSession }) {
  const eng = session.engine;
  const rec = eng?.record;
  if (!eng || !rec?.scheduleVersion) return <p className="muted">Import a schedule in Setup first.</p>;
  const roster = rec.scheduleVersion.schedule.participants;
  const bindings = new Map(rec.bindings.map((b) => [b.participantId, b]));
  const r = session.lastReconcile;
  const liveById = new Map((r?.live ?? []).map((l) => [l.runtimeId, l]));
  const inRound = rec.state === 'round_active';

  return (
    <div>
      <div className="row">
        <button className="primary" onClick={() => void session.reconcile()} disabled={!session.hasControl}>
          Read live attendees and match
        </button>
        <button className="secondary" onClick={() => void session.confirmExactMatches()} disabled={!session.hasControl || !(r?.candidates.length)}>
          Confirm all exact name matches
        </button>
        <span className="muted">
          {rec.lastReconciledAt ? `last read ${new Date(rec.lastReconciledAt).toLocaleTimeString()}` : 'not yet read'}
          {inRound ? ' · rooms are open: only the main session is visible, absences are not updated' : ''}
        </span>
      </div>
      {session.needsReconcileAfterReload && <div className="banner banner-warn">This controller was reloaded. Read live attendees before issuing any command.</div>}

      <h2>Roster</h2>
      <table>
        <thead>
          <tr>
            <th>Roster</th>
            <th>Role</th>
            <th>Matched attendee</th>
            <th>Suggestions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {roster.map((p) => {
            const b = bindings.get(p.participantId);
            const live = b ? liveById.get(b.runtimeId) : undefined;
            const cands = (r?.candidates ?? []).filter((c) => c.participantId === p.participantId);
            const absent = rec.absent.includes(p.participantId);
            const excluded = rec.excluded.includes(p.participantId);
            return (
              <tr key={p.participantId}>
                <td>
                  {p.displayName} <span className="muted">({p.participantId})</span>
                </td>
                <td>{p.role}</td>
                <td>
                  {excluded ? 'excluded' : b ? `${live?.displayName ?? b.runtimeId}${b.stale ? ' (stale)' : ''} · ${b.boundBy === 'auto_persistent' ? 'auto' : 'confirmed'}` : absent ? <span className="status status-absent">absent</span> : <span className="status status-unknown">unmatched</span>}
                </td>
                <td>
                  {!b &&
                    cands.map((c) => {
                      const l = liveById.get(c.runtimeId);
                      return (
                        <div key={c.runtimeId} className="row">
                          <span>
                            {l?.displayName ?? c.runtimeId} <span className="muted">({c.reasons.join('; ')})</span>
                          </span>
                          <button className="secondary" onClick={() => void session.confirm(c)} disabled={!session.hasControl}>
                            Confirm
                          </button>
                        </div>
                      );
                    })}
                </td>
                <td>
                  {!excluded && !absent && (
                    <button className="secondary" onClick={() => void session.markAbsent(p.participantId)} disabled={!session.hasControl}>
                      Mark absent
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h2>Attendees not on the roster</h2>
      {r && r.unmatchedLive.length === 0 && <p className="muted">None.</p>}
      <ul>
        {(r?.unmatchedLive ?? []).map((id) => {
          const l = liveById.get(id);
          return (
            <li key={id}>
              {l?.displayName ?? id} <span className="muted">{l?.platformRole ?? ''}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
